import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { HttpAdminApi } from "../../../interface/admin-api";
import Toast from "../../../utils/Toast";

interface PropsType {
  openModal: boolean;
  closeModal: () => void;
  broadSeq: string;
  eventInfo: any;
  callback: any;
}

const adminApi = new HttpAdminApi();

const GameInfoModule = ({ openModal, closeModal, broadSeq, eventInfo, callback }: PropsType) => {
  const toastRef: any = useRef();

  const [gameFlag, setGameFlag] = useState("");

  const [touched, setTouched] = useState<boolean[]>([]);
  const [quizTouched, setQuizTouched] = useState(false);

  const [ladderNum, setLadderNum] = useState(2);
  const [ladderInputs, setLadderInputs] = useState<string[]>(["", ""]);

  const [choiceQuiz, setChoiceQuiz] = useState("");
  const [choiceAnswers, setChoiceAnswers] = useState<string[]>(["", "", "", ""]);

  const [oxQuiz, setOxQuiz] = useState("");
  const [oxAnswer, setOxAnswer] = useState<"O" | "X" | null>(null);

  const [shortQuiz, setShortQuiz] = useState("");
  const [shortAnswer, setShortAnswer] = useState("");
  const [shortAnswerTouched, setShortAnswerTouched] = useState(false);

  const isDisabled = eventInfo.event_status !== "CREATE";

  useEffect(() => {
    if (eventInfo) {
      setGameFlag(eventInfo.event_condition.game_kind);
    }
  }, [eventInfo]);

  useEffect(() => {
    if (eventInfo.event_condition.game_kind === "ladder") {
      setLadderNum(eventInfo.game_info.ladder_num);
      setLadderInputs(eventInfo.game_info.ladder_inputs);
    } else if (eventInfo.event_condition.game_kind === "choice") {
      setChoiceQuiz(eventInfo.game_info.quiz);
      setChoiceAnswers([eventInfo.game_info.answer, ...eventInfo.game_info.candidate]);
    } else if (eventInfo.event_condition.game_kind === "oxquiz") {
      setOxQuiz(eventInfo.game_info.quiz);
      setOxAnswer(eventInfo.game_info.answer);
    } else if (eventInfo.event_condition.game_kind === "short") {
      setShortQuiz(eventInfo.game_info.quiz);
      setShortAnswer(eventInfo.game_info.answer);
    }
  }, [eventInfo, openModal]);

  useEffect(() => {
    setLadderInputs((prev) => {
      const newArr = [...prev];
      if (ladderNum > prev.length) {
        // 추가
        return [...newArr, ...Array(ladderNum - prev.length).fill("")];
      } else if (ladderNum < prev.length) {
        // 삭제
        return newArr.slice(0, ladderNum);
      }
      return prev;
    });

    setTouched((prev) => {
      if (ladderNum > prev.length) {
        // 추가 (모두 false 로 추가)
        return [...prev, ...Array(ladderNum - prev.length).fill(false)];
      } else if (ladderNum < prev.length) {
        // 삭제
        return prev.slice(0, ladderNum);
      }
      return prev;
    });
  }, [ladderNum]);

  const updateGameInfo = async () => {
    if (gameFlag === "ladder") {
      const isAnyEmpty = ladderInputs.some((v) => !v.trim());

      if (isAnyEmpty) {
        setTouched((prev) => {
          const updated = [...prev];
          ladderInputs.forEach((v, i) => {
            if (!v.trim()) updated[i] = true;
          });
          return updated;
        });
        return;
      }
    } else if (gameFlag === "choice") {
      const isQuizEmpty = !choiceQuiz.trim();
      const isAnswersEmpty = choiceAnswers.some((v) => !v.trim());

      if (isQuizEmpty) {
        setQuizTouched(true);
      }

      if (isAnswersEmpty) {
        setTouched((prev) => {
          const updated = [...prev];
          choiceAnswers.forEach((v, i) => {
            if (!v.trim()) updated[i] = true;
          });
          return updated;
        });
      }

      if (isQuizEmpty || isAnswersEmpty) {
        return;
      }
    } else if (gameFlag === "oxquiz") {
      if (!oxQuiz || !oxAnswer) {
        if (!oxQuiz) setQuizTouched(true);
        return;
      }
    } else if (gameFlag === "short") {
      if (!shortQuiz || !shortAnswer) {
        if (!shortQuiz) setQuizTouched(true);
        if (!shortAnswer) setShortAnswerTouched(true);
        return;
      }
    }

    if (!window.confirm("게임 정보를 변경하시겠습니까?")) return;

    const param = {
      command: "update_game",
      broad_seq: broadSeq,
      event_seq: eventInfo.sk,
      game_info: {
        ...(gameFlag === "ladder" && {
          ladder_num: ladderNum,
          ladder_inputs: ladderInputs,
        }),
        ...(gameFlag === "choice" && {
          quiz: choiceQuiz,
          answer: choiceAnswers[0],
          candidate: [...choiceAnswers.slice(1)],
        }),
        ...(gameFlag === "oxquiz" && {
          quiz: oxQuiz,
          answer: oxAnswer,
        }),
        ...(gameFlag === "short" && {
          quiz: shortQuiz,
          answer: shortAnswer,
        }),
      },
    };

    const res = await adminApi.post(param);
    if (res.code === "200") {
      callback({ command: "update_game", info: eventInfo });
      toastRef.current?.toast("게임 업데이트 성공", "success", 3000, {
        vertical: "top",
        horizontal: "center",
      });
      closeModal();
    }
  };

  const convertGameKind = (gameKind: string) => {
    switch (gameKind) {
      case "ladder":
        return "사다리타기";
      case "choice":
        return "4지선다";
      case "oxquiz":
        return "O,X퀴즈";
      case "short":
        return "주관식퀴즈";
    }
  };

  return (
    <>
      <Modal open={openModal} onClose={closeModal} aria-labelledby="modal-title" aria-describedby="modal-description">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "400px",
            bgcolor: "background.paper",
            border: "0px solid #000",
            borderRadius: "4px",
            boxShadow: 24,
            p: 2,
          }}
        >
          <Stack spacing={2} useFlexGap>
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
                <>
                  게임 정보 (
                  <Box component="span" sx={{ color: "red", fontWeight: 700 }}>
                    {convertGameKind(eventInfo.event_condition.game_kind)}
                  </Box>
                  ){" "}
                  {eventInfo.event_condition.game_kind !== "ladder" && (
                    <Box component="span" sx={{ color: "blue", fontWeight: 700 }}>
                      ({eventInfo.event_condition.is_fcfs ? "선착순" : "일반"})
                    </Box>
                  )}
                </>
              </Typography>
              <Box
                component="img"
                src="/images/icon-close.png"
                alt="닫기"
                sx={{
                  width: 15.84,
                  height: 16.95,
                  objectFit: "cover",
                  cursor: "pointer",
                }}
                onClick={closeModal}
              />
            </Stack>
            {eventInfo.event_condition.game_kind === "ladder" && (
              <>
                <TextField
                  label="이름 개수(최소 2, 최대 6)"
                  value={ladderNum}
                  variant="outlined"
                  type="number"
                  size="small"
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: 2,
                    max: 6,
                  }}
                  sx={{ width: "35%" }}
                  fullWidth
                  disabled={isDisabled}
                  onChange={(e) => setLadderNum(Number(e.target.value))}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      setLadderNum(2);
                    }
                  }}
                />
                <Grid container rowSpacing={1} columnSpacing={2}>
                  {ladderInputs.map((value, idx) => (
                    <Grid item xs={4} key={idx}>
                      <TextField
                        label={`이름 ${idx + 1}(최대 2글자)`}
                        value={value}
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        inputProps={{
                          maxLength: 2,
                        }}
                        error={touched[idx] && !value}
                        helperText={touched[idx] && !value ? "필수 입력입니다." : ""}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const newInputs = [...ladderInputs];
                          newInputs[idx] = e.target.value;
                          setLadderInputs(newInputs);
                        }}
                        onBlur={() => {
                          const t = [...touched];
                          t[idx] = true;
                          setTouched(t);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            {eventInfo.event_condition.game_kind === "choice" && (
              <>
                <TextField
                  label="문제"
                  value={choiceQuiz}
                  variant="outlined"
                  type="search"
                  size="small"
                  required
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  multiline
                  maxRows={5}
                  disabled={isDisabled}
                  error={quizTouched && !choiceQuiz}
                  helperText={quizTouched && !choiceQuiz ? "필수 입력입니다." : ""}
                  onChange={(e) => setChoiceQuiz(e.target.value)}
                  onBlur={(e) => {
                    setQuizTouched(true);
                    if (!e.target.value) {
                      setChoiceQuiz("");
                    }
                  }}
                />
                <Grid container spacing={1}>
                  {choiceAnswers.map((value, idx) => (
                    <Grid item xs={3} key={idx}>
                      <TextField
                        label={idx === 0 ? "정답" : `후보 ${idx}`}
                        value={value}
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        disabled={isDisabled}
                        error={touched[idx] && !value}
                        helperText={touched[idx] && !value ? "필수 입력입니다." : ""}
                        onChange={(e) => {
                          const newInputs = [...choiceAnswers];
                          newInputs[idx] = e.target.value;
                          setChoiceAnswers(newInputs);
                        }}
                        onBlur={() => {
                          const t = [...touched];
                          t[idx] = true;
                          setTouched(t);
                        }}
                        sx={
                          idx === 0
                            ? {
                                "& .MuiInputLabel-root": {
                                  color: "#00A676",
                                },

                                "& .MuiInputLabel-root.Mui-focused": {
                                  color: "#00A676",
                                },

                                "& .MuiInputLabel-root.Mui-error": {
                                  color: "red",
                                },

                                "& .MuiInputLabel-root.Mui-disabled": {
                                  color: "#00A676",
                                  opacity: 1, // MUI 기본 opacity 0.38 때문에 추가
                                },

                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "#00A676",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#00A676",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#00A676",
                                  },

                                  "&.Mui-error fieldset": {
                                    borderColor: "red",
                                  },
                                  "&.Mui-error:hover fieldset": {
                                    borderColor: "red",
                                  },
                                  "&.Mui-focused.Mui-error fieldset": {
                                    borderColor: "red",
                                  },
                                  "&.Mui-disabled fieldset": {
                                    borderColor: "#00A676",
                                    opacity: 1,
                                  },
                                },
                              }
                            : {}
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            {eventInfo.event_condition.game_kind === "oxquiz" && (
              <>
                <TextField
                  label="문제"
                  value={oxQuiz}
                  variant="outlined"
                  type="search"
                  size="small"
                  required
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  multiline
                  maxRows={5}
                  disabled={isDisabled}
                  error={quizTouched && !oxQuiz}
                  helperText={quizTouched && !oxQuiz ? "필수 입력입니다." : ""}
                  onChange={(e) => setOxQuiz(e.target.value)}
                  onBlur={(e) => {
                    setQuizTouched(true);
                    if (!e.target.value) {
                      setOxQuiz("");
                    }
                  }}
                />
                <FormControl error={!oxAnswer} component="fieldset" fullWidth>
                  <FormLabel
                    component="legend"
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    정답 선택
                  </FormLabel>

                  <Stack direction="row" spacing={2} useFlexGap>
                    <Button variant={oxAnswer === "O" ? "contained" : "outlined"} onClick={() => setOxAnswer("O")}>
                      O
                    </Button>
                    <Button variant={oxAnswer === "X" ? "contained" : "outlined"} onClick={() => setOxAnswer("X")}>
                      X
                    </Button>
                  </Stack>

                  {!oxAnswer && <FormHelperText>필수 선택입니다.</FormHelperText>}
                </FormControl>
              </>
            )}
            {eventInfo.event_condition.game_kind === "short" && (
              <>
                <TextField
                  label="문제"
                  value={shortQuiz}
                  variant="outlined"
                  type="search"
                  size="small"
                  required
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  multiline
                  maxRows={5}
                  disabled={isDisabled}
                  error={quizTouched && !shortQuiz}
                  helperText={quizTouched && !shortQuiz ? "필수 입력입니다." : ""}
                  onChange={(e) => setShortQuiz(e.target.value)}
                  onBlur={(e) => {
                    setQuizTouched(true);
                    if (!e.target.value) {
                      setShortQuiz("");
                    }
                  }}
                />
                <TextField
                  label="정답"
                  value={shortAnswer}
                  variant="outlined"
                  type="search"
                  size="small"
                  required
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  multiline
                  maxRows={5}
                  disabled={isDisabled}
                  error={shortAnswerTouched && !shortAnswer}
                  helperText={shortAnswerTouched && !shortAnswer ? "필수 입력입니다." : ""}
                  onChange={(e) => setShortAnswer(e.target.value)}
                  onBlur={(e) => {
                    setShortAnswerTouched(true);
                    if (!e.target.value) {
                      setShortAnswer("");
                    }
                  }}
                />
              </>
            )}
            <Stack direction="row" spacing={2} useFlexGap sx={{ justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={updateGameInfo} disabled={isDisabled}>
                변경
              </Button>
              <Button variant="outlined" onClick={closeModal}>
                취소
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
      <Toast ref={toastRef} />
    </>
  );
};

export default GameInfoModule;
